/**
 * FiltersPanel — side panel showing scope toggle, groupBy selector and per-facet
 * filter sections.
 *
 * Rendered as `position: absolute` overlaid on the body's right edge (UI-SPEC §2.15).
 * The parent (PromptStudioDialog) mounts/unmounts it via the Filtres button.
 *
 * @see B-popup-frontend.md step 10a, UI-SPEC §2.15
 */
import { useState } from 'react';
import { Box, Checkbox } from '@mui/material';
import type { FilterState, Facet, GroupBy } from './filterModel';
import { EMPTY_FILTER, facetCounts, matchOption, rowLanguage, rowVocabKeys, sortVocabKeys, vocabKeyLabel, familyPlaceLabels } from './filterModel';
import type { Family } from './promptStudioModel';
import { AXIS_COLOR, AXIS_ORDER } from './promptStudioModel';
import type { Brand, Location } from '@traaker/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FiltersPanelProps {
  state: FilterState;
  onChange: (s: FilterState) => void;
  /** All families (unfiltered) — used for facet counts. */
  families: Family[];
  brand: Brand;
  hasMultipleBrands: boolean;
  /** Current free-text search query — used for facet counts. */
  q?: string;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AXIS_LABEL: Record<string, string> = {
  narrative: 'Image',
  authority: 'Autorité',
  referral: 'Recommandation',
  comparison: 'Comparaison',
};

const STATUS_LABEL: Record<string, string> = {
  wait: 'En attente',
  ok: 'Classé',
  '?': 'Non classé',
};

const GROUPBY_OPTIONS: { key: GroupBy; label: string; requiresAll?: boolean }[] = [
  { key: 'axis', label: 'Axe' },
  { key: 'brand', label: 'Marque', requiresAll: true },
  { key: 'vocab', label: 'Thème / Cas / Critère' },
  { key: 'tag', label: 'Tag' },
  { key: 'zone', label: 'Lieu' },
];

const FACET_ORDER: Facet[] = ['brand', 'axis', 'vocab', 'tags', 'zone', 'language', 'target', 'status'];

const FACET_LABEL: Record<Facet, string> = {
  brand: 'Marques',
  axis: 'Axe',
  vocab: 'Thème / Cas / Critère',
  tags: 'Tags',
  zone: 'Lieu',
  language: 'Langue',
  target: 'Cible',
  status: 'Statut',
};

function countActive(f: FilterState): number {
  return Object.values(f.facets).reduce((n, arr) => n + (arr?.length ?? 0), 0) + (f.scope === 'tenant' ? 1 : 0);
}

// ---------------------------------------------------------------------------
// FacetSection
// ---------------------------------------------------------------------------

export interface FacetSectionProps {
  /** A prompt facet, or another panel's (the Conversations tab's) with its own `label`. */
  facet: Facet | string;
  label?: string;
  options: string[];
  counts: Record<string, number>;
  selected: string[];
  onChange: (values: string[]) => void;
  labelFor: (value: string) => React.ReactNode;
}

export function FacetSection({ facet, label, options, counts, selected, onChange, labelFor }: FacetSectionProps) {
  const [open, setOpen] = useState(() => selected.length > 0 || facet === 'axis' || facet === 'tags');
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  if (options.length <= 1 && selected.length === 0) return null;

  const totalLabel = label ?? FACET_LABEL[facet as Facet];
  const filtered = query
    ? options.filter((v) => matchOption(labelFor(v) as string ?? v, query))
    : options;
  const visible = showAll || filtered.length <= 6 ? filtered : filtered.slice(0, 6);
  const moreCount = filtered.length - visible.length;

  function toggle(v: string) {
    const next = selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v];
    onChange(next);
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Header */}
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          p: 0,
          pb: 0.5,
        }}
      >
        <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', flex: 1, textAlign: 'left' }}>
          {totalLabel}
        </Box>
        {selected.length > 0 && (
          <Box sx={{ fontSize: 10, fontWeight: 600, bgcolor: '#7c3aed', color: '#fff', borderRadius: 999, px: 0.75, py: 0.1 }}>
            {selected.length}
          </Box>
        )}
        <Box sx={{ fontSize: 11, color: 'text.disabled' }}>{options.length}</Box>
        <Box sx={{ fontSize: 10, color: 'text.disabled' }}>{open ? '⌃' : '⌄'}</Box>
      </Box>

      {open && (
        <>
          {/* Search field when > 6 options */}
          {options.length > 6 && (
            <Box
              component="input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Chercher parmi ${options.length} ${totalLabel.toLowerCase()}`}
              sx={{
                width: '100%',
                fontSize: 11.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                mb: 0.5,
                outline: 'none',
                fontFamily: 'inherit',
                '&:focus': { borderColor: '#7c3aed' },
              }}
            />
          )}

          {visible.length === 0 && (
            <Box sx={{ fontSize: 11.5, color: 'text.disabled', py: 0.5 }}>
              Aucun résultat pour « {query} »
            </Box>
          )}

          {visible.map((v) => (
            <Box
              key={v}
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                py: 0.25,
                cursor: 'pointer',
                opacity: (counts[v] ?? 0) === 0 && !selected.includes(v) ? 0.5 : 1,
              }}
            >
              <Checkbox
                size="small"
                checked={selected.includes(v)}
                onChange={() => toggle(v)}
                sx={{
                  p: 0.25,
                  color: 'divider',
                  '&.Mui-checked': { color: '#7c3aed' },
                }}
              />
              <Box sx={{ flex: 1, fontSize: 12.5, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {labelFor(v)}
              </Box>
              <Box sx={{ fontSize: 11, color: 'text.disabled', ml: 0.5 }}>
                {counts[v] ?? 0}
              </Box>
            </Box>
          ))}

          {moreCount > 0 && (
            <Box
              component="button"
              onClick={() => setShowAll(true)}
              sx={{ fontSize: 11.5, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, mt: 0.25 }}
            >
              {moreCount} de plus
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FiltersPanel
// ---------------------------------------------------------------------------

export function FiltersPanel({
  state,
  onChange,
  families,
  brand,
  hasMultipleBrands,
  q = '',
  onClose,
}: FiltersPanelProps) {
  const nActive = countActive(state);

  function setFacet(facet: Facet, values: string[]) {
    onChange({
      ...state,
      facets: { ...state.facets, [facet]: values.length ? values : undefined },
    });
  }

  function setGroupBy(groupBy: GroupBy) {
    onChange({ ...state, groupBy });
  }

  function setScope(scope: 'brand' | 'tenant') {
    // Scope back to brand: clear Marque facet and groupBy 'brand'
    const nextFacets = scope === 'brand'
      ? { ...state.facets, brand: undefined }
      : state.facets;
    const nextGroupBy = scope === 'brand' && state.groupBy === 'brand'
      ? 'axis' as GroupBy
      : state.groupBy;
    onChange({ ...state, scope, facets: nextFacets, groupBy: nextGroupBy });
  }

  function handleReset() {
    onChange(EMPTY_FILTER);
  }

  // Collect options per facet from all families
  const axisOptions = AXIS_ORDER.filter((ax) =>
    families.some((f) => f.root.axis === ax),
  );
  const statusOptions = (['wait', 'ok', '?'] as string[]).filter((s) =>
    families.some((f) =>
      f.rows.some((r) => {
        if (r.axis === 'pending') return s === 'wait';
        if (r.classifiedAt) return s === 'ok';
        return s === '?';
      }),
    ),
  );
  const tagOptions = Array.from(
    new Set(families.flatMap((f) => f.rows.flatMap((r) => r.tags ?? []))),
  ).sort();
  const languageOptions = Array.from(
    new Set(families.flatMap((f) => f.rows.map((r) => rowLanguage(r, brand.strategy)))),
  ).sort();

  const brandOptions = Array.from(new Set(families.map((f) => f.root.brandId)));

  // Counts per facet
  const locations: Location[] = (brand as any).locations ?? [];
  const counts = (facet: Facet) => facetCounts(families, q, state, facet, locations, brand.strategy);
  const axisCounts = counts('axis');
  const statusCounts = counts('status');
  const tagCounts = counts('tags');
  const languageCounts = counts('language');
  // Thème / Cas d'usage / Critère: the `${axis}|${term}` keys the list rows carry (Couverture's terms).
  const vocabOptions = sortVocabKeys(
    [...new Set(families.flatMap((f) => f.rows.filter((r) => r.status === 'active').flatMap((r) => rowVocabKeys(r, brand.strategy))))],
    brand.strategy,
  );
  const vocabCounts = counts('vocab');
  // Lieu: placeKey → its display (« 12 Rue de Rivoli (Paris) », Mondial first).
  const placeLabels = new Map<string, string>();
  for (const f of families) for (const [k, label] of familyPlaceLabels(f, locations)) if (!placeLabels.has(k)) placeLabels.set(k, label);
  const zoneOptions = [...placeLabels.keys()].sort((a, b) =>
    a === '∅' ? -1 : b === '∅' ? 1 : (placeLabels.get(a) ?? a).localeCompare(placeLabels.get(b) ?? b, 'fr'));
  const zoneCounts = counts('zone');

  const facetsVisible: Facet[] = FACET_ORDER.filter((f) => {
    if (f === 'brand') return state.scope === 'tenant' && hasMultipleBrands;
    if (f === 'axis') return axisOptions.length > 0;
    if (f === 'vocab') return vocabOptions.length > 0;
    if (f === 'target') return false; // lot F
    if (f === 'tags') return tagOptions.length > 0;
    if (f === 'zone') return zoneOptions.length > 0;
    if (f === 'language') return languageOptions.length > 0;
    if (f === 'status') return statusOptions.length > 0;
    return false;
  });

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 12,
        width: 340,
        maxHeight: 'calc(100% - 18px)',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 16px 40px rgba(0,0,0,.16)',
        zIndex: 10,
        p: 2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,.12)', borderRadius: 2 },
      }}
    >
      {/* Head */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>
          {/* i18n: promptStudio.filters.title */}
          Filtres
        </Box>
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          {families.length} prompts
        </Box>
        {nActive > 0 && (
          <Box
            component="button"
            onClick={handleReset}
            sx={{ fontSize: 12, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0 }}
          >
            {/* i18n: promptStudio.filters.reset */}
            Tout effacer
          </Box>
        )}
        <Box
          component="button"
          onClick={onClose}
          title="Fermer"
          sx={{ fontSize: 14, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary', p: 0, lineHeight: 1 }}
        >
          ✕
        </Box>
      </Box>

      {/* Scope toggle (multi-brand tenants only) */}
      {hasMultipleBrands && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
            {/* i18n: promptStudio.filters.marques */}
            Marques
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Quiet segmented control */}
            <Box
              component="button"
              onClick={() => setScope('brand')}
              sx={{
                fontSize: 12,
                border: '1px solid',
                borderColor: state.scope === 'brand' ? '#7c3aed' : 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 0.4,
                bgcolor: state.scope === 'brand' ? 'rgba(124,58,237,.08)' : 'transparent',
                color: state.scope === 'brand' ? '#7c3aed' : 'text.secondary',
                cursor: 'pointer',
              }}
            >
              {/* i18n: promptStudio.filters.thisBrand */}
              Cette marque
            </Box>
            <Box
              component="button"
              onClick={() => setScope('tenant')}
              sx={{
                fontSize: 12,
                border: '1px solid',
                borderColor: state.scope === 'tenant' ? '#7c3aed' : 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 0.4,
                bgcolor: state.scope === 'tenant' ? 'rgba(124,58,237,.08)' : 'transparent',
                color: state.scope === 'tenant' ? '#7c3aed' : 'text.secondary',
                cursor: 'pointer',
              }}
            >
              {/* i18n: promptStudio.filters.allBrands */}
              Toutes les marques · {brandOptions.length}
            </Box>
          </Box>
        </Box>
      )}

      {/* GroupBy selector */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
          {/* i18n: promptStudio.filters.groupBy */}
          Grouper par
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {GROUPBY_OPTIONS.filter((o) => !o.requiresAll || state.scope === 'tenant').map((o) => (
            <Box
              key={o.key}
              component="button"
              onClick={() => setGroupBy(o.key)}
              sx={{
                fontSize: 12,
                border: '1px solid',
                borderColor: state.groupBy === o.key ? '#7c3aed' : 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.3,
                bgcolor: state.groupBy === o.key ? 'rgba(124,58,237,.08)' : 'transparent',
                color: state.groupBy === o.key ? '#7c3aed' : 'text.secondary',
                cursor: 'pointer',
              }}
            >
              {o.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1.5 }} />

      {/* Facet sections */}
      {facetsVisible.map((facet) => {
        const selected = state.facets[facet] ?? [];

        if (facet === 'axis') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={axisOptions}
              counts={axisCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: AXIS_COLOR[v as keyof typeof AXIS_COLOR], display: 'inline-block', flexShrink: 0 }} />
                  {AXIS_LABEL[v] ?? v}
                </Box>
              )}
            />
          );
        }

        if (facet === 'status') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={statusOptions}
              counts={statusCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => STATUS_LABEL[v] ?? v}
            />
          );
        }

        if (facet === 'tags') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={tagOptions}
              counts={tagCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => v}
            />
          );
        }

        if (facet === 'vocab') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={vocabOptions}
              counts={vocabCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={vocabKeyLabel}
            />
          );
        }

        if (facet === 'zone') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={zoneOptions}
              counts={zoneCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => placeLabels.get(v) ?? v}
            />
          );
        }

        if (facet === 'language') {
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={languageOptions}
              counts={languageCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => v}
            />
          );
        }

        if (facet === 'brand') {
          const brandCounts = counts('brand');
          return (
            <FacetSection
              key={facet}
              facet={facet}
              options={brandOptions}
              counts={brandCounts}
              selected={selected}
              onChange={(vals) => setFacet(facet, vals)}
              labelFor={(v) => v}
            />
          );
        }

        return null;
      })}

      {/* Réinitialiser button (appears when any filter active) */}
      {nActive > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 0.5 }}>
          <Box
            component="button"
            onClick={handleReset}
            sx={{
              width: '100%',
              fontSize: 12.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              py: 0.6,
              bgcolor: 'transparent',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(0,0,0,.03)' },
            }}
          >
            {/* i18n: promptStudio.filters.resetAll */}
            Réinitialiser
          </Box>
        </Box>
      )}
    </Box>
  );
}
